// src/main.js
import { createApp } from 'vue';
import App from './App.vue';
import router from './router';
import store from './store'; // 导入 Vuex store

// 导入 axios 并配置全局拦截器
import axios from 'axios';

// 导入 Element Plus 样式和中文配置
import 'element-plus/dist/index.css';
import 'element-plus/es/components/message/style/css';
import 'element-plus/es/components/message-box/style/css';
import ElementPlus from 'element-plus';
import zhCn from 'element-plus/dist/locale/zh-cn.mjs';

// token续订状态管理
let isRefreshing = false; // 是否正在刷新token
let failedQueue = []; // 待重试的请求队列

// 处理待重试队列
const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// 配置 axios 全局请求拦截器
axios.interceptors.request.use(
  config => {
    // 从 store 中获取 token
    const token = store.getters.token;
    if (token) {
      // 在请求头中添加 Authorization
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// 配置 axios 全局响应拦截器
axios.interceptors.response.use(
  response => {
    return response;
  },
  async error => {
    const originalRequest = error.config;
    
    // 处理 401 未授权错误（token 过期或无效）
    if (error.response && error.response.status === 401) {
      // 如果是刷新token的请求失败，或者已经重试过，直接登出
      if (originalRequest.url.includes('/auth/refresh') || originalRequest._retry) {
        // 清除本地 token 并更新认证状态
        store.dispatch('logout');
        console.warn('登录已过期，请重新登录');
        
        // 跳转到登录页面
        if (router.currentRoute.value.path !== '/login') {
          router.push('/login');
        }
        
        return Promise.reject(error);
      }
      
      // 标记为重试请求
      originalRequest._retry = true;
      
      if (!isRefreshing) {
        isRefreshing = true;
        
        try {
          const response = await axios.post('/api/auth/refresh');
          
          if (response.data.success && response.data.token) {
            const newToken = response.data.token;
            store.dispatch('login', newToken);
            console.log('Token续订成功，重试原始请求');
            
            // 更新原始请求的token
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            
            // 处理队列中的请求
            processQueue(null, newToken);
            
            // 重试原始请求
            return axios(originalRequest);
          }
        } catch (refreshError) {
          console.error('Token续订失败:', refreshError);
          processQueue(refreshError, null);
          
          // 清除本地 token 并更新认证状态
          store.dispatch('logout');
          
          // 跳转到登录页面
          if (router.currentRoute.value.path !== '/login') {
            router.push('/login');
          }
          
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      } else {
        // 如果正在刷新，将请求加入队列
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(axios(originalRequest));
            },
            reject: (err) => {
              reject(err);
            }
          });
        });
      }
    }
    
    return Promise.reject(error);
  }
);

// 防抖函数
const debounce = (fn, delay) => {
let timer
    return (...args) => {
    if (timer) {
        clearTimeout(timer)
    }
    timer = setTimeout(() => {
        fn(...args)
    }, delay)
    }
}
// 扩展 ResizeObserver
const _ResizeObserver = window.ResizeObserver;
window.ResizeObserver = class ResizeObserver extends _ResizeObserver{
    constructor(callback) {
    callback = debounce(callback, 200);
    super(callback);
    }
}

const app = createApp(App);

app.use(store); // 使用 Vuex store
app.use(router);
app.use(ElementPlus, {
  locale: zhCn,
});
app.mount('#app');


